import {
  addDoc,
  collection,
  getDocs,
  limit,
  query,
  where,
} from 'firebase/firestore';
import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytes,
} from 'firebase/storage';
import heic2any from 'heic2any';

import { db, storage } from '../firebase/config';
import { convertImageToPdf } from './convertImageToPdf';
import { stripMappingMeta } from './vatMapping';

// SHA-256 of file contents — used to detect duplicates across uploads.
export async function hashFile(file) {
  try {
    const buffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    return Array.from(new Uint8Array(hashBuffer))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    return null;
  }
}

// Convert HEIC/HEIF to JPEG when needed; otherwise pass-through.
export async function convertHeicIfNeeded(file) {
  const fileType = (file.type || '').toLowerCase();
  const fileNameLower = (file.name || '').toLowerCase();
  const isHeicMime = fileType.includes('heic') || fileType.includes('heif');
  const isHeicExt = fileNameLower.endsWith('.heic') || fileNameLower.endsWith('.heif');
  if (!(isHeicMime || (!fileType && isHeicExt))) return file;

  try {
    let converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 });
    if (Array.isArray(converted)) converted = converted[0];
    const convertedBlob =
      converted instanceof Blob ? converted : new Blob([converted], { type: 'image/jpeg' });
    return new File(
      [convertedBlob],
      file.name.replace(/\.(heic|heif)$/i, '.jpg'),
      { type: 'image/jpeg' },
    );
  } catch (innerErr) {
    const msg = String(innerErr?.message || '').toLowerCase();
    if (msg.includes('already browser readable')) return file;
    throw innerErr;
  }
}

// Returns true if a file with this hash already exists for the company.
export async function isDuplicate(companyId, fileHash) {
  if (!companyId || !fileHash) return false;
  try {
    const dupQuery = query(
      collection(db, 'companies', companyId, 'invoices'),
      where('fileHash', '==', fileHash),
      limit(1),
    );
    const snap = await getDocs(dupQuery);
    return !snap.empty;
  } catch {
    return false;
  }
}

// Uploads the file to a temp folder for AI extraction; image inputs are also
// converted to PDF for the final stored artifact.
// Returns: { tempPath, extractionUrl, finalFile, fileHash }
export async function prepareForExtraction(rawFile, companyId) {
  const working = await convertHeicIfNeeded(rawFile);
  const fileHash = await hashFile(working);
  const isPdf =
    working.name.toLowerCase().endsWith('.pdf') ||
    working.type === 'application/pdf';

  const safeName = `ext_${Date.now()}_${working.name.replace(/[^\w.-]+/g, '_')}`;
  const tempPath = `invoices/${companyId}/temp/${safeName}`;
  const tempRef = ref(storage, tempPath);
  await uploadBytes(tempRef, working);
  const extractionUrl = await getDownloadURL(tempRef);

  let finalFile = working;
  if (!isPdf) {
    finalFile = await convertImageToPdf(working);
  }
  return { tempPath, extractionUrl, finalFile, fileHash };
}

export async function cleanupTemp(tempPath) {
  if (!tempPath) return;
  try {
    await deleteObject(ref(storage, tempPath));
  } catch {
    /* ignore */
  }
}

function safeFileSegment(val) {
  return String(val || '')
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^\w-]/g, '')
    .substring(0, 80);
}

export function parseIssueDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  const d = new Date(value);
  if (!Number.isNaN(d.getTime())) return d;
  const m = /^([0-3]?\d)[/-]([0-1]?\d)[/-](\d{4})$/.exec(String(value));
  if (m) {
    const dd = new Date(parseInt(m[3], 10), parseInt(m[2], 10) - 1, parseInt(m[1], 10));
    if (!Number.isNaN(dd.getTime())) return dd;
  }
  return null;
}

// Final upload of the cleaned/PDF file + Firestore record.
export async function saveInvoiceToFirestore({
  companyId,
  user,
  finalFile,
  fields,
  fileHash,
}) {
  const cleanFields = stripMappingMeta(fields);
  const issueDate = parseIssueDate(cleanFields.issue_date) || new Date();
  const vat = safeFileSegment((cleanFields.issuer_vat || 'NA').toUpperCase()) || 'NA';
  const company = safeFileSegment(cleanFields.issuer_company_name || 'Company') || 'Company';
  const dateStr = issueDate.toISOString().split('T')[0];
  const code = Math.random().toString(36).slice(2, 8).toUpperCase();
  const uniqueFileName = `${vat}-${dateStr}-${company}-${code}.pdf`;
  const finalRef = ref(storage, `invoices/${companyId}/${uniqueFileName}`);
  await uploadBytes(finalRef, finalFile);
  const finalURL = await getDownloadURL(finalRef);

  const savedHash = fileHash || (finalFile ? await hashFile(finalFile) : null);
  await addDoc(collection(db, 'companies', companyId, 'invoices'), {
    fileName: uniqueFileName,
    url: finalURL,
    extracted: cleanFields,
    issue_date: issueDate,
    uploadedAt: new Date().toISOString(),
    uploadedBy: {
      uid: user?.uid || '',
      name: user?.displayName || '',
      email: user?.email || '',
    },
    ...(savedHash ? { fileHash: savedHash } : {}),
  });

  return { uniqueFileName, url: finalURL };
}
